import React, { useContext } from "react";
import { BuilderContext, useDrawer } from "react-flow-builder";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import { Button, Grid, Title, Text, Tabs, SegmentedControl, Modal, ActionIcon } from "@mantine/core";
import { CTextInput, CMultiSelect, CSelect, CCheckbox, CTextarea } from "@supportinitiative/react-hook-form-mantine";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { getColumnsToMergeLOV, getDataset, inlineQueryOperators, operators } from "../../ReconsetUtils";
import { getId } from "../../../utilities";
import { useDatasets, useLoaders } from "../../../hooks";
import { ReconsetCVMContext } from "../../ReconsetCVM";
import { DataLoadActionType, ReconDataLoadActionSchema } from "@supportinitiative/common";

/* 
const inputSchema = object({
  conditions: array().of(
    object({
      column: string().required(),
      value: string().required(),
    })
  ),
});

const derivationSchema = object({
  rules: array().of(
    object({
      rule: string().required(),
      ruleId: string().required(),
    })
  ),
}); */

function Inputs({ name, control, disabled }: { name: ""; control: Control<any>; disabled: boolean }) {
  const conditionsArray = useFieldArray({
    control,
    name: `inputs.conditions`,
  });
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Conditions
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              conditionsArray.append({
                left: "",
                operator: "=",
                right: "",
                leftId: getId(5),
                rightId: getId(5),
              });
            }}
            disabled={disabled || false}
            style={{
              display: disabled || false ? "none" : "",
            }}
          >
            <IconPlus />
          </ActionIcon>
        </div>

        {conditionsArray.fields.map((field, index) => {
          return (
            <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }}>
              <Grid.Col span={4}>
                <CTextInput
                  name={`inputs.conditions.${index}.column`}
                  control={control}
                  label={index === 0 ? "Column" : ""}
                  disabled={disabled}
                  showErrorText={false}
                  required
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <CTextInput
                  name={`inputs.conditions.${index}.value`}
                  control={control}
                  label={index === 0 ? "Value" : ""}
                  disabled={disabled}
                  showErrorText={false}
                />
              </Grid.Col>

              <Grid.Col
                span={1}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <ActionIcon
                  size="sm"
                  variant="white"
                  color="gray"
                  onClick={() => {
                    conditionsArray.remove(index);
                  }}
                  disabled={disabled}
                  style={{
                    display: disabled ? "none" : "",
                  }}
                >
                  <IconTrash />
                </ActionIcon>
              </Grid.Col>
            </Grid>
          );
        })}
      </div>
    </div>
  );
}

/* function Derivations({
  name,
  control,
  disabled,
}: {
  name: "";
  control: Control<any>;
  disabled: boolean;
}) {
  const rulesArray = useFieldArray({
    control,
    name: `derivations.rules`,
  });
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Rules
          </Text>
          <ActionIcon
            onClick={() => {
              rulesArray.append({
                rule: "",
                ruleId: getId(5),
              });
            }}
            disabled={disabled || false}
            style={{
              display: disabled || false ? "none" : "",
            }}
          >
            <IconPlus />
          </ActionIcon>
        </div>

        {rulesArray.fields.map((field, index) => {
          return (
            <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }}>
              <Grid.Col span={8}>
                <CTextInput
                  name={`derivations.rules.${index}.rule`}
                  control={control}
                  label={index === 0 ? "Rule" : ""}
                  disabled={disabled}
                  showErrorText={false}
                  required
                />
              </Grid.Col>
              <Grid.Col
                span={1}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <ActionIcon
                  onClick={() => {
                    rulesArray.remove(index);
                  }}
                  disabled={disabled}
                  style={{
                    display: disabled ? "none" : "",
                  }}
                >
                  <IconTrash />
                </ActionIcon>
              </Grid.Col>
            </Grid>
          );
        })}
      </div>
    </div>
  );
}
 */

function Derivation({ name, control, disabled }: { name: ""; control: Control<any>; disabled: boolean }) {
  const mappingsArray = useFieldArray({
    control,
    name: `derivation.mappings`,
  });
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }}>
        <Grid.Col span={12}>
          <CTextInput
            name={`derivation.rule`}
            control={control}
            label="Rule"
            required
            showErrorText={false}
            disabled={disabled || false}
          />
        </Grid.Col>
      </Grid>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Columns
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              mappingsArray.append({
                column: "",
                external: "",
              });
            }}
            disabled={disabled || false}
            style={{
              display: disabled || false ? "none" : "",
            }}
          >
            <IconPlus />
          </ActionIcon>
        </div>

        {mappingsArray.fields.map((field, index) => {
          return (
            <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }}>
              <Grid.Col span={4}>
                <CTextInput
                  name={`custom.mappings.${index}.column`}
                  control={control}
                  disabled={disabled}
                  showErrorText={false}
                  label={index === 0 ? "Operator" : ""}
                  required
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <CTextInput
                  name={`custom.mappings.${index}.external`}
                  control={control}
                  disabled={disabled}
                  showErrorText={false}
                  label={index === 0 ? "External" : ""}
                  required
                />
              </Grid.Col>
              <Grid.Col
                span={1}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <ActionIcon
                  size="sm"
                  variant="white"
                  color="gray"
                  onClick={() => {
                    mappingsArray.remove(index);
                  }}
                  disabled={disabled}
                  style={{
                    display: disabled ? "none" : "",
                  }}
                >
                  <IconTrash />
                </ActionIcon>
              </Grid.Col>
            </Grid>
          );
        })}
      </div>
    </div>
  );
}

function External({ name, control, disabled }: { name: ""; control: Control<any>; disabled: boolean }) {
  const mappingsArray = useFieldArray({
    control,
    name: `external.mappings`,
  });
  const conditionsArray = useFieldArray({
    control,
    name: `external.conditions`,
  });
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }}>
        <Grid.Col span={12}>
          <CTextInput
            name={`external.table`}
            control={control}
            label="Table"
            required
            showErrorText={false}
            disabled={disabled || false}
          />
        </Grid.Col>
      </Grid>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Columns
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              mappingsArray.append({
                column: "",
                external: "",
              });
            }}
            disabled={disabled || false}
            style={{
              display: disabled || false ? "none" : "",
            }}
          >
            <IconPlus />
          </ActionIcon>
        </div>

        {mappingsArray.fields.map((field, index) => {
          return (
            <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }} key={index}>
              <Grid.Col span={4}>
                <CTextInput
                  name={`external.mappings.${index}.column`}
                  control={control}
                  disabled={disabled}
                  showErrorText={false}
                  label={index === 0 ? "Column" : ""}
                  required
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <CTextInput
                  name={`external.mappings.${index}.external`}
                  control={control}
                  disabled={disabled}
                  showErrorText={false}
                  label={index === 0 ? "External" : ""}
                  required
                />
              </Grid.Col>
              <Grid.Col
                span={1}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <ActionIcon
                  size="sm"
                  variant="white"
                  color="gray"
                  onClick={() => {
                    mappingsArray.remove(index);
                  }}
                  disabled={disabled}
                  style={{
                    display: disabled ? "none" : "",
                  }}
                >
                  <IconTrash />
                </ActionIcon>
              </Grid.Col>
            </Grid>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Conditions
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              conditionsArray.append({
                left: "",
                operator: "=",
                right: "",
              });
            }}
            disabled={disabled || false}
            style={{
              display: disabled || false ? "none" : "",
            }}
          >
            <IconPlus />
          </ActionIcon>
        </div>

        {conditionsArray.fields.map((field, index) => {
          return (
            <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }} key={index}>
              <Grid.Col span={4}>
                <CTextInput
                  name={`external.conditions.${index}.left`}
                  control={control}
                  label={index === 0 ? "Left" : ""}
                  disabled={disabled}
                  showErrorText={false}
                  required
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <CSelect
                  name={`external.conditions.${index}.operator`}
                  control={control}
                  searchable
                  data={[...operators, ...inlineQueryOperators]}
                  label={index === 0 ? "Operator" : ""}
                  disabled={disabled}
                  showErrorText={false}
                  required
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <CTextInput
                  name={`external.conditions.${index}.right`}
                  control={control}
                  label={index === 0 ? "Right" : ""}
                  disabled={disabled}
                  showErrorText={false}
                />
              </Grid.Col>

              <Grid.Col
                span={1}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <ActionIcon
                  size="sm"
                  variant="white"
                  color="gray"
                  onClick={() => {
                    conditionsArray.remove(index);
                  }}
                  disabled={disabled}
                  style={{
                    display: disabled ? "none" : "",
                  }}
                >
                  <IconTrash />
                </ActionIcon>
              </Grid.Col>
            </Grid>
          );
        })}
      </div>
    </div>
  );
}

function Custom({ name, control, disabled }: { name: ""; control: Control<any>; disabled: boolean }) {
  const conditionsArray = useFieldArray({
    control,
    name: `custom.conditions`,
  });
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }}>
        <Grid.Col span={12}>
          <CTextInput
            name={`custom.config`}
            control={control}
            label="Config"
            required
            showErrorText={false}
            disabled={disabled || false}
          />
        </Grid.Col>
      </Grid>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Text fw={500} style={{ fontSize: "14px" }}>
            Conditions
          </Text>
          <ActionIcon
            size="sm"
            variant="white"
            color="gray"
            onClick={() => {
              conditionsArray.append({
                left: "",
                operator: "=",
                right: "",
              });
            }}
            disabled={disabled || false}
            style={{
              display: disabled || false ? "none" : "",
            }}
          >
            <IconPlus />
          </ActionIcon>
        </div>

        {conditionsArray.fields.map((field, index) => {
          return (
            <Grid align="stretch" gutter={"xs"} style={{ marginBottom: "5px" }} key={index}>
              <Grid.Col span={4}>
                <CTextInput
                  name={`custom.conditions.${index}.left`}
                  control={control}
                  label={index === 0 ? "Left" : ""}
                  disabled={disabled}
                  showErrorText={false}
                  required
                />
              </Grid.Col>
              <Grid.Col span={3}>
                <CSelect
                  name={`custom.conditions.${index}.operator`}
                  control={control}
                  searchable
                  data={[...operators, ...inlineQueryOperators]}
                  label={index === 0 ? "Operator" : ""}
                  disabled={disabled}
                  showErrorText={false}
                  required
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <CTextInput
                  name={`custom.conditions.${index}.right`}
                  control={control}
                  label={index === 0 ? "Right" : ""}
                  disabled={disabled}
                  showErrorText={false}
                />
              </Grid.Col>

              <Grid.Col
                span={1}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <ActionIcon
                  size="sm"
                  variant="white"
                  color="gray"
                  onClick={() => {
                    conditionsArray.remove(index);
                  }}
                  disabled={disabled}
                  style={{
                    display: disabled ? "none" : "",
                  }}
                >
                  <IconTrash />
                </ActionIcon>
              </Grid.Col>
            </Grid>
          );
        })}
      </div>
    </div>
  );
}

function getDatasetId(dataset: string, recon: any) {
  if (dataset === "PRIMARY") {
    return recon.definition.primaryDataset.id;
  } else if (dataset === "SECONDARY") {
    return recon.definition.secondaryDataset.id;
  }
}

function getDatasetColumns(dataset: string, recon: any) {
  if (dataset === "PRIMARY") {
    return recon.definition.primaryDataset.columns || [];
  } else if (dataset === "SECONDARY") {
    return recon.definition.secondaryDataset.columns || [];
  }
}

function getDatasetAdditionalColumns(dataset: string, recon: any) {
  if (dataset === "PRIMARY") {
    return recon.definition.primaryDataset.additionalColumns || [];
  } else if (dataset === "SECONDARY") {
    return recon.definition.secondaryDataset.additionalColumns || [];
  }
}

function Dataset({
  name,
  control,
  disabled,
  dataset,
}: {
  name: "";
  control: Control<any>;
  disabled: boolean;
  dataset: string;
}) {
  const { state: reconsetFormState } = React.useContext(ReconsetCVMContext);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid align="stretch" gutter={"xs"} mb="lg">
        <Grid.Col span={12}>
          <CTextarea
            name={`fromDataset.dataFetchCondition`}
            control={control}
            label="Data Fetch Condition"
            required
            showErrorText={false}
            disabled={disabled || false}
            minRows={4}
          />
        </Grid.Col>
      </Grid>
    </div>
  );
}

const DataLoadRecon: React.FC = (props: any) => {
  const { selectedNode: node } = useContext(BuilderContext);
  const { state: reconsetFormState } = React.useContext(ReconsetCVMContext);

  const { closeDrawer: cancel, saveDrawer: save } = useDrawer();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<DataLoadActionType>({
    defaultValues: node?.data || {
      name: "",
      dataset: "PRIMARY",
      type: "DATASET",
    },
    criteriaMode: "all",
    resolver: yupResolver(ReconDataLoadActionSchema),
  });

  const dataLoadType = useWatch({
    control,
    name: `type`,
  });

  const dataset = useWatch({
    control,
    name: `dataset`,
  });

  const { data: loaders } = useLoaders(reconsetFormState.entityId);

  const onSubmitForm = async (data: any) => {
    try {
      console.log("onSubmit", data);
      save?.(data);
    } catch (error: any) {
      console.log("onSubmit error", error);
    }
  };

  return (
    <Modal centered size="xl" opened={true} onClose={cancel} title={<Title order={4}>Data Load</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={node?.disabled || false}
            />
          </Grid.Col>
        </Grid>
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CSelect
              name={`dataset`}
              control={control}
              label={"Dataset"}
              data={
                reconsetFormState?.recon?.definition?.secondaryDataset
                  ? [
                      {
                        value: "PRIMARY",
                        label: "Primary",
                      },
                      {
                        value: "SECONDARY",
                        label: "Secondary",
                      },
                      {
                        value: "OTHER",
                        label: "Other",
                      },
                    ]
                  : [
                      {
                        value: "PRIMARY",
                        label: "Primary",
                      },
                      {
                        value: "OTHER",
                        label: "Other",
                      },
                    ]
              }
              showErrorText={false}
              disabled={node?.disabled || false}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CSelect
              name={`type`}
              control={control}
              label={"Type"}
              data={[
                {
                  value: "DATASET",
                  label: "Dataset",
                },
                /*                 {
                  value: "FROM_INPUT",
                  label: "From Input",
                },
                {
                  value: "DERIVED",
                  label: "Derived",
                }, */
                {
                  value: "EXTERNAL",
                  label: "External",
                },
                {
                  value: "CUSTOM",
                  label: "Custom",
                },
              ]}
              showErrorText={false}
              required
              disabled={node?.disabled || false}
            />
          </Grid.Col>
        </Grid>

        {dataset === "OTHER" ? (
          <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
            <Grid.Col span={12}>
              <CTextInput
                name="otherDataset"
                control={control}
                label="Other Dataset Name"
                showErrorText={false}
                disabled={node?.disabled || false}
              />
            </Grid.Col>
          </Grid>
        ) : (
          <></>
        )}

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name="loaderId"
              label="Loader"
              placeholder="Select Loader"
              control={control}
              data={
                loaders && loaders.length > 0
                  ? loaders.map((loader: any) => {
                      return { label: loader.name, value: loader.id };
                    })
                  : []
              }
              disabled={node?.disabled || false}
            />
          </Grid.Col>
        </Grid>
        {dataLoadType === "EXTERNAL" ? (
          <External name={""} control={control} disabled={node?.disabled} />
        ) : dataLoadType === "CUSTOM" ? (
          <Custom name={""} control={control} disabled={node?.disabled} />
        ) : dataLoadType === "DATASET" ? (
          <Dataset name={""} control={control} disabled={node?.disabled} dataset={dataset} />
        ) : (
          <></>
        )}
        <Grid align="stretch" gutter={"xs"} mt="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                node?.disabled
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await ReconDataLoadActionSchema.validate(values, {
                          abortEarly: false,
                        });

                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
};

export default DataLoadRecon;
